import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
 
import { toast } from "sonner";
import RichTextEditor from "@/components/ui/RichTextEditor";

import {
  useEditPageMutation,
  useGetPageBySlugQuery,
  useDeletePageMutation,
} from "@/features/api/pageApi";

const PageTab = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [previewImage, setPreviewImage] = useState("");

  const [input, setInput] = useState({
    title: "",
    slug: "",
    metaDescription: "",
    content: "",
    altText: "",
    featuredImage: "",
    status: "draft",
  });

  const { data: pageData, isLoading: pageDataLoading, refetch } = useGetPageBySlugQuery(slug, {
    skip: !slug,
    refetchOnMountOrArgChange: true,
  });

  const [editPage, { isLoading }] = useEditPageMutation();
  const [deletePageBySlug] = useDeletePageMutation();

  useEffect(() => {
    if (pageData?.page) {
      const p = pageData.page;
      setInput({
        title: p.title ?? "",
        slug: p.slug ?? "",
        content: typeof p.content === "object" ? JSON.stringify(p.content) : p.content ?? "",
        altText: p.altText ?? "",
        metaDescription: p.metaDescription ?? "",
        featuredImage: p.featuredImage ?? "",
        status: p.status ?? "draft",
      });

      if (p.featuredImage) {
        setPreviewImage(p.featuredImage);
      }
    }
  }, [pageData]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setInput((prev) => ({ ...prev, [name]: value }));
  };

  const handleSlugChange = (e) => {
    const value = e.target.value;
    const updatedSlug = value
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");

    setInput((prev) => ({ ...prev, slug: updatedSlug }));
  };

  const handleEditorChange = (newContent) => {
    setInput((prev) => ({ ...prev, content: newContent }));
  };

  const updatePageHandler = async () => {
    if (!input.title.trim() || !input.slug.trim() || !input.content.trim()) {
      toast.error("Title, slug, and content are required!");
      return;
    }

    const formData = new FormData();
    Object.entries(input).forEach(([key, value]) => {
      if (key === "featuredImage" && value instanceof File) {
        formData.append(key, value);
      } else if (value) {
        formData.append(key, value);
      }
    });

    try {
      await editPage({ slug, formData }).unwrap();
      toast.success("Page updated successfully.");
      refetch();
    } catch (err) {
      toast.error("Failed to update the page.");
      console.error(err);
    }
  };

  const handleDeletePage = async () => {
    if (!window.confirm("Are you sure you want to delete this page?")) return;

    try {
      await deletePageBySlug(slug).unwrap();
      toast.success("Page deleted successfully.");
      navigate("/admin/pages");
    } catch (err) {
      toast.error("Failed to delete the page.");
      console.error(err);
    }
  };

  const selectThumbnail = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/") || file.size > 2 * 1024 * 1024) {
      toast.error("Invalid image. Only images under 2MB are allowed.");
      return;
    }

    setInput((prev) => ({ ...prev, featuredImage: file }));

    const reader = new FileReader();
    reader.onloadend = () => setPreviewImage(reader.result);
    reader.readAsDataURL(file);
  };

  if (pageDataLoading) return <Loader2 className="h-4 w-4 animate-spin mx-auto mt-10" />;

  return (
    <Card className="p-4">
      <CardHeader className="flex flex-row justify-between items-center">
        <CardDescription>Update your page details</CardDescription>
        <div className="space-x-2">
          <Button variant="outline" onClick={() => window.open(`/${input.slug}`, "_blank")}>
            Preview
          </Button>
          <Button variant="destructive" onClick={handleDeletePage}>
            Remove Page
          </Button>
        </div>
      </CardHeader>

      <CardContent>
        <div className="space-y-4 mt-5">
          <div>
            <Label htmlFor="title">Title</Label>
            <Input name="title" id="title" value={input.title} onChange={handleInputChange} placeholder="Page title" />
          </div>

          <div>
            <Label htmlFor="metaDescription">Meta Description</Label>
            <Input
              name="metaDescription"
              id="metaDescription"
              value={input.metaDescription}
              onChange={handleInputChange}
              placeholder="Short description for SEO"
            />
          </div>

          <div>
            <Label htmlFor="slug">Slug</Label>
            <Input name="slug" id="slug" value={input.slug} onChange={handleSlugChange} placeholder="page-slug" />
          </div>

          <div>
            <Label htmlFor="content">Content</Label>
            <RichTextEditor value={input.content} onChange={handleEditorChange} />
          </div>

          <div>
            <Label htmlFor="featuredImage">Featured Image</Label>
            <Input type="file" onChange={selectThumbnail} accept="image/*" className="w-fit" />
            {previewImage && (
              <img src={previewImage} className="w-64 my-2 rounded-md" alt={input.altText || "Preview"} />
            )}
          </div>

          <div>
            <Label htmlFor="altText">Alt Text</Label>
            <Input name="altText" id="altText" value={input.altText} onChange={handleInputChange} placeholder="Image alt text" />
          </div>

          <div className="pt-2">
            <Label>Status</Label>
            <div className="flex items-center gap-4 mt-2">
              <div className="flex items-center gap-2">
                <input
                  type="radio"
                  id="draft"
                  name="status"
                  value="draft"
                  checked={input.status === "draft"}
                  onChange={handleInputChange}
                />
                <Label htmlFor="draft">Draft</Label>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="radio"
                  id="published"
                  name="status"
                  value="published"
                  checked={input.status === "published"}
                  onChange={handleInputChange}
                />
                <Label htmlFor="published">Published</Label>
              </div>
            </div>
          </div>

          <div className="flex space-x-2 mt-4">
            <Button variant="outline" onClick={() => navigate("/admin/pages")}>Back</Button>
            <Button disabled={isLoading} onClick={updatePageHandler}>
              {isLoading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Saving...</> : "Save"}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default PageTab;
